import { useState } from 'react'

import { useField } from 'formik'
import {
  ContactFragment,
  EmployeeSelectorInitialValuesDocument,
  UniversalExportsExporterFilterKindEnum,
  UniversalExportsExporterFilterOption,
} from 'generated/resources/graphql'
import { compact } from 'lodash'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import SelectOrCreate from 'modules/core/components/SelectOrCreate'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { FormFilter } from 'modules/universal_exports/components/ExportSidepanel/types'

interface FilterSelectorProps {
  name: string
  kind: UniversalExportsExporterFilterKindEnum
  label: string
  options: UniversalExportsExporterFilterOption[]
  onFilterChange?: () => void
}

const FilterSelector: React.FC<FilterSelectorProps> = ({
  name,
  kind,
  label,
  options,
  onFilterChange,
}) => {
  const [_, { value: filter }, { setValue }] = useField<FormFilter>(name)
  const [employees, setEmployees] = useState<EmployeeForSelectFragmentNoAccess[] | undefined>()
  const [vendors, setVendors] = useState<ContactFragment[]>([])
  const { company } = useCurrentSession()

  const { hasCompensations } = useHasCompensations()

  const trackFilterChange = (
    filterName: string,
    filterKind: UniversalExportsExporterFilterKindEnum,
    filterLabel: string,
    valueCount: number
  ) => {
    if (hasCompensations) {
      track(trackingEvents.compensations.changeExportFilter, {
        filter_name: filterName,
        filter_kind: filterKind,
        filter_label: filterLabel,
        value_count: valueCount,
      })
    }
  }

  const onChange = (values?: string[]) => {
    setValue({ ...filter, values: values ?? [] })
    trackFilterChange(filter.name, kind, label, values?.length ?? 0)
    onFilterChange?.()
  }

  const onChangeDate = (values?: string[]) => {
    if (values) {
      // Normalize the date range to include the start of day and end of day disregarding timezone as it could cause incosistencies when filtering by date range
      const from = values[0] ? moment.utc(values[0]).startOf('day').toISOString() : undefined
      const to = values[1] ? moment.utc(values[1]).endOf('day').toISOString() : undefined

      onChange(compact([from, to]))
    } else {
      onChange(values)
    }
  }

  // Set initial employees if filter is for employees
  useQuery(EmployeeSelectorInitialValuesDocument, {
    variables: {
      companyId: company.id,
      ids: filter.values.map(Number),
    },
    skip: kind != 'employees' || employees !== undefined, // if employees is not undefined means this query has already done, so not need to repeat.
    onCompleted: (data) => {
      const employeeFragments = data.employees.employeeNames
      if (kind == 'employees' && employeeFragments) {
        setEmployees(employeeFragments)
      }
    },
  })

  const onEmployeeChange = (newEmployees?: EmployeeForSelectFragmentNoAccess[]) => {
    setEmployees(newEmployees ?? [])
    setValue({ ...filter, values: (newEmployees ?? []).map((emp) => emp.id + '') })
    trackFilterChange(filter.name, kind, label, (newEmployees ?? []).length)
    onFilterChange?.()
  }

  const onVendorChange = (newVendors: ContactFragment[]) => {
    setVendors(newVendors)
    setValue({ ...filter, values: newVendors.map((vendor) => vendor.id.toString()) })
    trackFilterChange(filter.name, kind, label, newVendors.length)
    onFilterChange?.()
  }

  switch (kind) {
    case 'multiplechoice':
      return (
        <SelectOrCreate.Popover
          mandatory={false}
          label={label}
          placeholder=""
          onValuesChange={onChange}
          values={filter.values}
          isMultiSelect
          onChange={() => null}
        >
          {options.map((option) => {
            return (
              <SelectOrCreate.Option
                key={option.value}
                label={option.label}
                value={option.value}
                searchValue={option.label}
              >
                {option.label}
              </SelectOrCreate.Option>
            )
          })}
        </SelectOrCreate.Popover>
      )
    case 'employees':
      return (
        <EmployeeSelector
          multiple
          onSelect={onEmployeeChange}
          label={label}
          includeInactive
          emptyLabel=""
          hideSearchIcon
          value={employees ?? []}
        />
      )
    case 'vendors':
      return (
        <FinanceContactsSelector
          multiple
          disableCreate
          onSelect={onVendorChange}
          label={label}
          emptyLabel=""
          hideSearchIcon
          type="finance_contact"
          value={vendors}
          queryParams={{
            companyId: company.id,
            contactSearchType: 'vendor',
          }}
          variant={'gamma'}
        />
      )
    case 'singlechoice':
      return (
        <SelectOrCreate.Popover
          mandatory={false}
          label={label}
          placeholder=""
          onValuesChange={onChange}
          values={filter.values}
          isMultiSelect={false}
          onChange={() => null}
        >
          {options.map((option) => {
            return (
              <SelectOrCreate.Option
                key={option.value}
                label={option.label}
                value={option.value}
                searchValue={option.label}
              >
                {option.label}
              </SelectOrCreate.Option>
            )
          })}
        </SelectOrCreate.Popover>
      )
    case 'datetimerange':
      const [from, to] = filter.values

      // Just pass the raw values to DayPicker since they are already normalized.
      const fromDate = from?.split('T')[0]
      const toDate = to?.split('T')[0]

      return (
        <DayPicker
          mode="range"
          format="ui"
          valueFormat="backend"
          value={[fromDate, toDate]}
          label={label}
          closeOnChange={false}
          onChange={(values) => onChangeDate(compact(values))}
          maxDate={new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 3)}
        />
      )
    default:
      return null
  }
}

export default FilterSelector
